import { useState, useRef } from 'react'
import { Modal, Input, Typography, Button, Checkbox, theme } from 'antd'
import { SearchOutlined, CloseOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../Stores'
import { CHAT_DRAWER_Z_INDEX } from '../ChatDrawer'
import ChatAvatar from '../../Components/common/ChatAvatar'
import OnlineIndicator from '../../Components/common/OnlineIndicator'

const { Text } = Typography

interface NewChatModalProps {
  open: boolean
  onClose: () => void
}

const NewChatModal = observer(({ open, onClose }: NewChatModalProps) => {
  const { userStore, chatStore } = useStore()
  const { token } = theme.useToken()
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [groupName, setGroupName] = useState('')
  const isNameManual = useRef(false)

  const currentId = userStore.currentUserId
  const query = search.toLowerCase().trim()

  const contacts = userStore.users.filter((u) => {
    if (u.id === currentId) return false
    if (!query) return true
    const fullName = `${u.firstName} ${u.lastName}`.toLowerCase()
    return fullName.includes(query)
  })

  // Автоназва групи: творець — теж учасник розмови, тож його ім'я
  // відкриває список («Я, Оксана, Дмитро»), як у Telegram
  const buildAutoName = (ids: string[]): string =>
    [userStore.currentUser?.firstName, ...ids.map((id) => userStore.getById(id)?.firstName)]
      .filter(Boolean)
      .join(', ')

  const toggleUser = (userId: string) => {
    setSelectedIds((prev) => {
      const next = prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]

      // Auto-generate group name when not manually edited
      if (!isNameManual.current && next.length >= 2) {
        setGroupName(buildAutoName(next))
      }
      if (next.length < 2) {
        setGroupName('')
        isNameManual.current = false
      }

      return next
    })
  }

  const handleCreate = () => {
    if (selectedIds.length === 0) return

    const firstId = selectedIds[0]
    if (selectedIds.length === 1 && firstId) {
      chatStore.createPrivateChat(firstId)
    } else {
      const finalName = groupName.trim() || buildAutoName(selectedIds)
      chatStore.createGroupChat(selectedIds, finalName)
    }
    reset()
    onClose()
  }

  const reset = () => {
    setSearch('')
    setSelectedIds([])
    setGroupName('')
    isNameManual.current = false
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const buttonLabel = selectedIds.length === 0
    ? 'Оберіть контакти'
    : selectedIds.length === 1
    ? 'Почати чат'
    : `Створити групу (${selectedIds.length})`

  return (
    <Modal
      title="Новий чат"
      open={open}
      onCancel={handleClose}
      zIndex={CHAT_DRAWER_Z_INDEX + 1}
      footer={
        <Button
          type="primary"
          block
          disabled={selectedIds.length === 0}
          onClick={handleCreate}
        >
          {buttonLabel}
        </Button>
      }
      width={400}
    >
      {/* Selected chips */}
      {selectedIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 6px', marginBottom: 12 }}>
          {selectedIds.map((id) => {
            const user = userStore.getById(id)
            if (!user) return null
            return (
              <div
                key={id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '3px 10px 3px 3px',
                  borderRadius: 16,
                  background: token.colorPrimaryBg,
                  border: `1px solid ${token.colorPrimaryBorder}`,
                  fontSize: 12,
                }}
              >
                <ChatAvatar name={`${user.firstName} ${user.lastName}`} src={user.avatarUrl} size={20} onTint />
                <span>{user.firstName}</span>
                <CloseOutlined
                  style={{ fontSize: 10, cursor: 'pointer', color: token.colorTextSecondary }}
                  onClick={() => toggleUser(id)}
                />
              </div>
            )
          })}
        </div>
      )}

      {/* Group name input — when 2+ selected */}
      {selectedIds.length >= 2 && (
        <>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>
          Назва групи
        </Text>
        <Input
          placeholder="Введіть назву…"
          value={groupName}
          onChange={(e) => {
            setGroupName(e.target.value)
            isNameManual.current = true
          }}
          maxLength={50}
          style={{ marginBottom: 10 }}
        />
        </>
      )}

      <Input
        placeholder="Пошук контактів…"
        prefix={<SearchOutlined style={{ color: token.colorTextQuaternary }} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        autoFocus
        style={{ marginBottom: 12 }}
      />

      <div
        className="chat-scroll"
        style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {contacts.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: token.colorTextSecondary }}>
            Контактів не знайдено
          </div>
        ) : (
          contacts.map((user) => {
            const isSelected = selectedIds.includes(user.id)
            const hasChat = !!chatStore.findPrivateChatWith(user.id)
            return (
              <div
                key={user.id}
                onClick={() => toggleUser(user.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 8px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  background: isSelected ? token.colorPrimaryBg : 'transparent',
                }}
              >
                <Checkbox checked={isSelected} />
                <div style={{ position: 'relative' }}>
                  <ChatAvatar
                    name={`${user.firstName} ${user.lastName}`}
                    src={user.avatarUrl}
                    size={36}
                    onTint={isSelected}
                  />
                  {user.status === 'online' && <OnlineIndicator size={8} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 13 }}>
                    {user.firstName} {user.lastName}
                  </Text>
                  {hasChat && (
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                      Вже є чат
                    </Text>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </Modal>
  )
})

export default NewChatModal
